import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, CheckCircle, Shield, Clock, MapPin, Award, AlertCircle, Wrench } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { findSubService, getMainService } from '../data/services';

const PHONE = "403-615-8770";

// Real DNB job photos - Calgary (served from /public/images/dnb/)
// Each entry maps a sub-service slug to its hero, mid-page, and (optional) lower-page image
// plus SEO-friendly, location-based alt text.
const customImages = {
  // ===== PLUMBING =====
  'plumbing-repairs-calgary': {
    hero: '/images/dnb/plumbing-repair-1.jpg',
    heroAlt: 'Calgary plumbing repair by DNB technician',
    midPage: '/images/dnb/plumbing-repair-2.jpg',
    midAlt: 'Close-up of repaired residential plumbing fixture in Calgary',
  },
  'bathroom-plumbing-calgary': {
    hero: '/images/dnb/bathroom-1.jpg',
    heroAlt: 'Bathroom plumbing rough-in completed by DNB Calgary',
    midPage: '/images/dnb/bathroom-2.jpg',
    midAlt: 'Calgary bathroom shower valve and supply lines installed by DNB',
  },
  'kitchen-plumbing-calgary': {
    hero: '/images/dnb/kitchen-1.jpg',
    heroAlt: 'Kitchen plumbing installation in a Calgary home by DNB',
    midPage: '/images/dnb/kitchen-2.jpg',
    midAlt: 'Under-sink kitchen plumbing connections completed by DNB Calgary',
  },
  'basement-roughins-calgary': {
    hero: '/images/dnb/basement-roughin-1.jpg',
    heroAlt: 'Basement plumbing rough-in by DNB in a Calgary home',
    midPage: '/images/dnb/basement-roughin-2.jpg',
    midAlt: 'Drain, vent and water lines roughed-in for a Calgary basement development',
    lowerPage: '/images/dnb/basement-roughin-3.jpg',
    lowerAlt: 'Under-slab drainage and PEX water lines for a Calgary basement bathroom',
  },
  'water-heater-replacement-calgary': {
    hero: '/images/dnb/water-heater-1.jpg',
    heroAlt: 'New hot water tank installed by DNB in Calgary',
    midPage: '/images/dnb/water-heater-2.jpg',
    midAlt: 'Calgary water heater replacement with new gas and water connections',
  },
  'tankless-water-heater-calgary': {
    hero: '/images/dnb/tankless-1.jpg',
    heroAlt: 'Wall-mounted tankless water heater installed in a Calgary home by DNB',
    midPage: '/images/dnb/water-heater-2.jpg',
    midAlt: 'Tankless water heater gas, water and venting connections in Calgary',
  },
  'water-softeners-calgary': {
    hero: '/images/dnb/water-softener-1.jpg',
    heroAlt: 'Whole-home water softener installed by DNB in Calgary',
  },
  'water-filtration-calgary': {
    hero: '/images/dnb/water-filtration-1.jpg',
    heroAlt: 'Whole-home water filtration system installed by DNB in Calgary',
  },
  'drain-cleaning-calgary': {
    hero: '/images/dnb/drain-cleaning-1.jpg',
    heroAlt: 'Calgary drain cleaning service performed by DNB',
  },
  'sewer-repair-calgary': {
    hero: '/images/dnb/sewer-1.jpg',
    heroAlt: 'Main sewer line service in a Calgary home by DNB',
  },
  'poly-b-replacement-calgary': {
    hero: '/images/dnb/polyb-1.jpg',
    heroAlt: 'Poly-B replacement with PEX piping by DNB Calgary',
    midPage: '/images/dnb/polyb-2.jpg',
    midAlt: 'New PEX manifold replacing old Poly-B plumbing in a Calgary home',
    lowerPage: '/images/dnb/polyb-3.jpg',
    lowerAlt: 'Calgary Poly-B pipe replacement detail showing clean PEX run by DNB',
  },
  'backflow-testing-calgary': {
    hero: '/images/dnb/backflow-1.jpg',
    heroAlt: 'Backflow preventer assembly tested by DNB in Calgary',
    midPage: '/images/dnb/backflow-2.jpg',
    midAlt: 'Certified backflow testing on a Calgary property by DNB',
  },
  'secondary-suite-plumbing-calgary': {
    hero: '/images/dnb/suite-1.jpg',
    heroAlt: 'Secondary suite plumbing rough-in in a Calgary basement by DNB',
    midPage: '/images/dnb/suite-2.jpg',
    midAlt: 'Legal secondary suite plumbing layout in Calgary by DNB',
  },
  // ===== HEATING =====
  'furnace-installation-repair-calgary': {
    hero: '/images/dnb/furnace-1.jpg',
    heroAlt: 'High-efficiency furnace installed by DNB in Calgary',
    midPage: '/images/dnb/furnace-2.jpg',
    midAlt: 'Calgary furnace heat exchanger inspection and repair by DNB',
  },
  'boiler-hydronic-calgary': {
    hero: '/images/dnb/boiler-1.jpg',
    heroAlt: 'Residential boiler and hydronic heating system installed in Calgary by DNB',
    midPage: '/images/dnb/boiler-2.jpg',
    midAlt: 'Calgary boiler manifold and circulators serviced by DNB',
  },
  'in-floor-radiant-heating-calgary': {
    hero: '/images/dnb/radiant-1.jpg',
    heroAlt: 'In-floor radiant heating tubing installed by DNB in Calgary',
    midPage: '/images/dnb/radiant-2.jpg',
    midAlt: 'Calgary hydronic radiant floor manifold installed by DNB',
  },
  'garage-heaters-calgary': {
    hero: '/images/dnb/garage-heater-1.jpg',
    heroAlt: 'Natural gas garage heater installed in a Calgary garage by DNB',
    midPage: '/images/dnb/garage-heater-2.jpg',
    midAlt: 'Garage unit heater venting and gas connection completed in Calgary',
  },
  'thermostats-controls-calgary': {
    hero: '/images/dnb/thermostat-1.jpg',
    heroAlt: 'Smart thermostat installed in a Calgary home by DNB',
  },
  // ===== GAS FITTING =====
  'gas-line-installation-calgary': {
    hero: '/images/dnb/gas-line-1.jpg',
    heroAlt: 'Black iron gas line installation in a Calgary home by DNB',
    midPage: '/images/dnb/gas-line-2.jpg',
    midAlt: 'Calgary gas line routing with shutoff and pressure-tested fittings',
  },
  'gas-safety-testing-calgary': {
    hero: '/images/dnb/gas-testing-1.jpg',
    heroAlt: 'Gas leak detection and safety testing in a Calgary home by DNB',
    midPage: '/images/dnb/gas-testing-2.jpg',
    midAlt: 'Calgary gas system pressure test performed by licensed DNB gas fitter',
  },
  'bbq-gas-lines-calgary': {
    hero: '/images/dnb/bbq-gas-1.jpg',
    heroAlt: 'Permanent natural gas BBQ line installed by DNB in Calgary',
    midPage: '/images/dnb/bbq-gas-2.jpg',
    midAlt: 'Calgary BBQ gas quick-connect and shutoff valve completed by DNB',
  },
  'gas-appliance-installation-calgary': {
    hero: '/images/dnb/gas-appliance-1.jpg',
    heroAlt: 'Gas appliance hookup completed in a Calgary home by DNB',
    midPage: '/images/dnb/gas-appliance-2.jpg',
    midAlt: 'Licensed gas fitter connecting a residential gas appliance in Calgary',
  },
  'gas-fireplace-calgary': {
    hero: '/images/dnb/fireplace-1.jpg',
    heroAlt: 'Gas fireplace connection completed by DNB in Calgary',
    midPage: '/images/dnb/fireplace-2.jpg',
    midAlt: 'Calgary gas fireplace installation with safe, code-compliant gas line',
  },
};

// Final fallback (only used if a slug above is missing). Pulls from the same real photos.
const heroImages = {
  'plumbing-repair': '/images/dnb/plumbing-repair-1.jpg',
  'bathroom': '/images/dnb/bathroom-1.jpg',
  'kitchen': '/images/dnb/kitchen-1.jpg',
  'basement': '/images/dnb/basement-roughin-1.jpg',
  'water-heater': '/images/dnb/water-heater-1.jpg',
  'tankless': '/images/dnb/tankless-1.jpg',
  'water-quality': '/images/dnb/water-softener-1.jpg',
  'drain': '/images/dnb/drain-cleaning-1.jpg',
  'sewer': '/images/dnb/sewer-1.jpg',
  'polyb': '/images/dnb/polyb-1.jpg',
  'backflow': '/images/dnb/backflow-1.jpg',
  'furnace': '/images/dnb/furnace-1.jpg',
  'boiler': '/images/dnb/boiler-1.jpg',
  'radiant': '/images/dnb/radiant-1.jpg',
  'garage': '/images/dnb/garage-heater-1.jpg',
  'thermostat': '/images/dnb/thermostat-1.jpg',
  'gas-line': '/images/dnb/gas-line-1.jpg',
  'bbq': '/images/dnb/bbq-gas-1.jpg',
  'gas-appliance': '/images/dnb/gas-appliance-1.jpg',
  'fireplace': '/images/dnb/fireplace-1.jpg',
};

export const SubServicePage = () => {
  const { mainService, subService } = useParams();
  
  const mainServiceData = getMainService(mainService);
  const subServiceData = findSubService(mainService, subService);
  
  if (!mainServiceData || !subServiceData) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Service Not Found</h1>
          <Link to="/" className="text-[#0F172A] hover:underline">Return to Homepage</Link>
        </div>
      </main>
    );
  }

  // Get custom images for this page if available
  const pageCustomImages = customImages[subServiceData.slug] || {};
  
  // Use custom hero image if available, otherwise fall back to heroImages mapping
  const heroImage = pageCustomImages.hero || heroImages[subServiceData.heroImage] || heroImages['plumbing-repair'];
  const heroAlt = pageCustomImages.heroAlt || `${subServiceData.name} in Calgary by DNB`;

  // Get additional images for mid-page and lower sections
  const midPageImage = pageCustomImages.midPage;
  const midPageAlt = pageCustomImages.midAlt || `${subServiceData.name} in progress in Calgary by DNB`;
  const lowerPageImage = pageCustomImages.lowerPage;
  const lowerPageAlt = pageCustomImages.lowerAlt || `${subServiceData.name} detail by DNB Calgary`;
  
  const introFirstSentence = subServiceData.intro.split('.')[0] + '.';

  return (
    <>
      <Helmet>
        <title>{subServiceData.metaTitle}</title>
        <meta name="description" content={subServiceData.metaDescription} />
        <link rel="canonical" href={`https://dnbplumbing.ca/${mainService}/${subService}`} />
      </Helmet>

      <main data-testid="sub-service-page" className="page-transition">
        {/* Hero Section */}
        <section className="relative pt-24 pb-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={heroImage} 
              alt={heroAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#0F172A]/85 to-[#0F172A]/70" />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <nav className="flex items-center gap-2 text-white/70 text-sm mb-6">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <span>/</span>
                <Link to={`/${mainService}`} className="hover:text-white transition-colors capitalize">
                  {mainServiceData.name}
                </Link>
                <span>/</span>
                <span className="text-white">{subServiceData.name}</span>
              </nav>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                  {subServiceData.name} <span className="text-[#0EA5E9]">in Calgary</span>
                </h1>
                
                <p className="text-white/90 text-lg sm:text-xl mb-8 max-w-2xl">
                  {introFirstSentence}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`tel:${PHONE}`}
                    data-testid="hero-call-btn"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Call Now: {PHONE}
                  </a>
                  <Link
                    to="/contact"
                    data-testid="hero-book-btn"
                    className="btn-secondary inline-flex items-center justify-center gap-2"
                  >
                    {subServiceData.cta}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="prose prose-lg max-w-none">
                {subServiceData.intro.split('\n\n').map((paragraph, idx) => (
                  <p key={idx} className="text-slate-600 text-lg leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Section 2 - Dynamic Title Based on Service Type */}
        <section className="py-16 section-tint">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="inline-flex p-3 bg-slate-100 rounded-xl mb-4">
                  {subServiceData.serviceType === 'repair' ? (
                    <AlertCircle className="w-7 h-7 text-[#0284C7]" />
                  ) : subServiceData.serviceType === 'compliance' ? (
                    <Shield className="w-7 h-7 text-[#0284C7]" />
                  ) : (
                    <Wrench className="w-7 h-7 text-[#0284C7]" />
                  )}
                </div>
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#0F172A] mb-6">
                  {subServiceData.section2Title}
                </h2>
                
                <ul className="space-y-4">
                  {subServiceData.section2Items && subServiceData.section2Items.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="p-1 bg-slate-100 rounded-full mt-1 flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-[#0284C7]" />
                      </div>
                      <span className="text-slate-700 text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Why This Matters */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200"
              >
                <h3 className="font-heading font-bold text-2xl text-[#0F172A] mb-6">
                  Why This Service Matters
                </h3>
                <div className="prose prose-lg">
                  {subServiceData.whyMatters.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-slate-600 leading-relaxed mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mid-Page Image Section - Only shown if custom midPageImage exists */}
        {midPageImage && (
          <section className="py-8 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl overflow-hidden shadow-lg"
              >
                <img 
                  src={midPageImage} 
                  alt={midPageAlt}
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                />
              </motion.div>
            </div>
          </section>
        )}

        {/* What We Do Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#0F172A]">
                What We Do
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {subServiceData.services && subServiceData.services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="dnb-card-soft"
                >
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-[#0F172A]/10 rounded-lg flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-[#0F172A]" />
                    </div>
                    <p className="text-slate-700 font-medium">{service}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Lower Page Image Section - Only shown if custom lowerPageImage exists */}
        {lowerPageImage && (
          <section className="py-8 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl overflow-hidden shadow-lg"
              >
                <img 
                  src={lowerPageImage} 
                  alt={lowerPageAlt}
                  className="w-full h-64 sm:h-80 object-cover"
                />
              </motion.div>
            </div>
          </section>
        )}

        {/* Why Modern Materials (Poly-B specific) */}
        {subServiceData.whyModernMaterials && (
          <section className="py-12 bg-[#0F172A]/5">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-4">
                  Why Modern Replacement Materials Are Better
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {subServiceData.whyModernMaterials}
                </p>
              </motion.div>
            </div>
          </section>
        )}

        {/* Why Choose DNB Section */}
        <section className="py-16 section-tint">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#0F172A] mb-6">
                  Why Choose DNB?
                </h2>
                <div className="prose prose-lg">
                  {subServiceData.whyChoose.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-slate-600 leading-relaxed mb-4 last:mb-0">
                      {paragraph}
                    </p>
                  ))}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
                  {[
                    { icon: MapPin, title: 'Local Calgary Company', desc: 'We know Calgary homes' },
                    { icon: Clock, title: 'Reliable Service', desc: 'We show up on time' },
                    { icon: Shield, title: 'Licensed & Insured', desc: 'Full coverage protection' },
                    { icon: Award, title: 'Quality Work', desc: 'Done right the first time' },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3">
                      <div className="p-2 bg-[#0F172A]/10 rounded-lg flex-shrink-0">
                        <item.icon className="w-5 h-5 text-[#0F172A]" />
                      </div>
                      <div>
                        <h4 className="font-bold text-[#0F172A]">{item.title}</h4>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111827] to-[#0F172A] rounded-2xl p-8 text-white"
              >
                <h3 className="font-heading font-bold text-2xl mb-6">
                  Ready to Get Started?
                </h3>
                <p className="text-white/90 mb-6">
                  Contact DNB today. We're here to help.
                </p>
                
                <div className="space-y-4">
                  <a
                    href={`tel:${PHONE}`}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    Call: {PHONE}
                  </a>
                  <Link
                    to="/contact"
                    className="btn-secondary w-full inline-flex items-center justify-center gap-2"
                  >
                    {subServiceData.cta}
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                </div>
                
                <p className="text-white/70 text-sm mt-6 text-center">
                  {subServiceData.ctaHelper}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 cta-banner relative">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
                {subServiceData.cta}
              </h2>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Fast response, fair pricing, quality work. Contact DNB now.
              </p>
              
              <div className="mb-8">
                <a 
                  href={`tel:${PHONE}`}
                  className="text-white text-3xl sm:text-4xl font-black hover:text-[#0EA5E9] transition-colors"
                >
                  {PHONE}
                </a>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={`tel:${PHONE}`}
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  Call Now
                </a>
                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Book Online
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              
              <p className="text-white/60 text-sm mt-6">
                {subServiceData.ctaHelper}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="font-heading font-bold text-2xl text-[#0F172A]">
                Other {mainServiceData.name} Services
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mainServiceData.subServices
                .filter(s => s.slug !== subService)
                .slice(0, 4)
                .map((service, index) => (
                  <motion.div
                    key={service.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link
                      to={`/${mainService}/${service.slug}`}
                      className="block p-5 dnb-card-soft hover:border-[#0284C7] transition-all group"
                    >
                      <h3 className="font-heading font-bold text-[#0F172A] group-hover:text-[#0F172A] transition-colors mb-2">
                        {service.name}
                      </h3>
                      <span className="text-[#0F172A] text-sm font-medium inline-flex items-center gap-1">
                        Learn More <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </motion.div>
                ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default SubServicePage;
